/**
 * Token model — a recursive tree with an integer cursor.
 *
 * The top-level equation is a flat list of `Token`s. Most tokens are
 * atoms (a single glyph). Structures (`struct`) carry editable **slots**;
 * each slot is itself a `Token[]` — a nested mini-equation — so a slot
 * can hold not just typed characters but operators, symbols, and other
 * structures (a `\sqrt` inside a fraction's numerator, etc.).
 *
 * Editing is expressed as a "focus path" + cursor in the host state
 * (`MathEditor`): the path locates which container (root or a specific
 * slot) the cursor indexes into. This file only defines the shape, the
 * factories, and the LaTeX emitter (which recurses through slots).
 *
 * `newline` and `align` are first-class tokens (not separators), so
 * the renderer's grid switch can format multi-line aligned equations
 * without a special codepath.
 */

import type { CategoryKey } from "./palette";

export type TokenKind =
  | "letter"
  | "upletter"
  | "num"
  | "punct"
  | "op"
  | "rel"
  | "arrow"
  | "greek"
  | "bigop"
  | "fn"
  | "struct"
  | "newline"
  | "align";

export interface BaseToken {
  kind: TokenKind;
  /** Visible glyph (for atoms). Structures carry no glyph. */
  g?: string;
  italic?: boolean;
  /** Horizontal margin multiplier — 0..0.5 of displaySize. */
  mx?: number;
  /** Size multiplier (used by `bigop` to scale ∑/∫/∏). */
  size?: number;
  /** Category key for colour-by-category mode. */
  cat?: CategoryKey;
}

export type StructKind =
  | "frac"
  | "sqrt"
  | "nthroot"
  | "subsup"
  | "integral"
  | "sum"
  | "lim"
  | "matrix"
  | "cases"
  | "aligned"
  | "vector"
  // 2026-05-28: added after the Perplexity research round flagged
  // these as frequently authored in practice but defaulted out of
  // every competitor's quick-access panel.
  | "abs"      // |x|     — absolute value
  | "parens"   // (x)     — explicit delimited group
  | "accent"   // x̂ / x⃗ / x̄ — accent mark over a single base slot
  | "binom";   // (n¦k)   — binomial coefficient (fraction without the rule)

export interface StructToken extends BaseToken {
  kind: "struct";
  /** Display name for the source line + caret position info. */
  name: string;
  /** Which structure to render — drives the glyph component. */
  structKind: StructKind;
  /**
   * Editable inline slots. Each key maps to a `Token[]` — the nested
   * mini-equation inside that slot (empty array = placeholder). The
   * renderer paints empty slots as dashed-purple boxes and renders
   * non-empty slots recursively; the LaTeX emitter emits each slot via
   * `equationToLatex(slot)`.
   *
   * Slot ordering for Tab cycling is declared separately by
   * `slotOrder` so that visual reading order (e.g. `b, sub, sup`
   * left-to-right for `subsup`) is independent of the property
   * iteration order.
   */
  slots: Record<string, Token[]>;
  /** Ordered slot keys for Tab navigation. */
  slotOrder: string[];
  /** Matrix dimensions (matrix only). Kept off `slots` so every slot
   *  value stays a clean `Token[]`. */
  rows?: number;
  cols?: number;
  /** Accent command (accent only) — the LaTeX name, e.g. "hat", "vec",
   *  "bar", "overline", "tilde", "dot". Drives the mark + emitted source. */
  accent?: string;
  /** Delimiter kind (parens only): which bracket pair wraps the body —
   *  "paren" (), "bracket" [], "brace" {}, "angle" ⟨⟩. */
  delim?: "paren" | "bracket" | "brace" | "angle";
}

/** LaTeX delimiter pairs for the `parens` struct, keyed by `delim`. */
const DELIMS: Record<string, [string, string]> = {
  paren: ["(", ")"],
  bracket: ["[", "]"],
  brace: ["\\{", "\\}"],
  angle: ["\\langle", "\\rangle"],
};

export type Token = BaseToken | StructToken;

/**
 * One hop into the token tree: token `tokenIdx` (a struct) of the
 * current container, then into its `slotKey` slot. A `FocusPath` is a
 * chain of these from the root; `[]` addresses the root token list.
 */
export interface SlotStep {
  tokenIdx: number;
  slotKey: string;
}
export type FocusPath = SlotStep[];

/** True when two focus paths address the same container. */
export function samePath(a: FocusPath, b: FocusPath): boolean {
  if (a.length !== b.length) return false;
  return a.every((s, i) => s.tokenIdx === b[i].tokenIdx && s.slotKey === b[i].slotKey);
}

/** True when `prefix` is an ancestor of (or equal to) `path`. */
export function isPathPrefix(prefix: FocusPath, path: FocusPath): boolean {
  if (prefix.length > path.length) return false;
  return prefix.every((s, i) => s.tokenIdx === path[i].tokenIdx && s.slotKey === path[i].slotKey);
}

/**
 * Resolve the token list addressed by `path` (the root list when the
 * path is empty). Returns an empty array if the path is invalid.
 */
export function getContainer(root: Token[], path: FocusPath): Token[] {
  let arr = root;
  for (const step of path) {
    const tok = arr[step.tokenIdx];
    if (!tok || tok.kind !== "struct") return [];
    arr = (tok as StructToken).slots[step.slotKey] ?? [];
  }
  return arr;
}

/**
 * Return a new root with the container addressed by `path` replaced by
 * `fn(container)`. Structurally shares everything off the path, so React
 * sees new references only where the edit landed.
 */
export function replaceContainer(
  root: Token[],
  path: FocusPath,
  fn: (arr: Token[]) => Token[],
): Token[] {
  if (path.length === 0) return fn(root);
  const [step, ...rest] = path;
  const tok = root[step.tokenIdx];
  if (!tok || tok.kind !== "struct") return root;
  const st = tok as StructToken;
  const childArr = st.slots[step.slotKey] ?? [];
  const newChild = replaceContainer(childArr, rest, fn);
  const next = [...root];
  next[step.tokenIdx] = { ...st, slots: { ...st.slots, [step.slotKey]: newChild } };
  return next;
}

/** Map Greek glyph → \cmd for source emission. */
const GREEK_MAP: Record<string, string> = {
  α: "\\alpha", β: "\\beta", γ: "\\gamma", δ: "\\delta",
  ε: "\\varepsilon", ζ: "\\zeta", η: "\\eta", θ: "\\theta",
  ι: "\\iota", κ: "\\kappa", λ: "\\lambda", μ: "\\mu",
  ν: "\\nu", ξ: "\\xi", π: "\\pi", ρ: "\\rho", σ: "\\sigma",
  τ: "\\tau", φ: "\\varphi", χ: "\\chi", ψ: "\\psi", ω: "\\omega",
  Γ: "\\Gamma", Δ: "\\Delta", Θ: "\\Theta", Λ: "\\Lambda",
  Π: "\\Pi", Σ: "\\Sigma", Φ: "\\Phi", Ψ: "\\Psi", Ω: "\\Omega",
  Ξ: "\\Xi", Υ: "\\Upsilon", ℓ: "\\ell",
};

const OP_MAP: Record<string, string> = {
  "×": "\\times", "÷": "\\div", "⋅": "\\cdot", "±": "\\pm",
  "∓": "\\mp", "∘": "\\circ", "∗": "\\ast", "⊕": "\\oplus",
  "⊗": "\\otimes", "⊙": "\\odot", "−": "-",
  // Emit canonical commands (not the raw Unicode glyph) for symbols the
  // parser accepts, so the source/export round-trips: e.g. \sum_{n}^{\infty}
  // stays \infty rather than collapsing to a literal ∞.
  "∞": "\\infty", "∂": "\\partial", "∇": "\\nabla",
  "∪": "\\cup", "∩": "\\cap", "∖": "\\setminus", "∅": "\\emptyset",
  "ℏ": "\\hbar", "ℵ": "\\aleph", "⋆": "\\star",
  "∀": "\\forall", "∃": "\\exists", "∄": "\\nexists",
  "¬": "\\neg", "∧": "\\wedge", "∨": "\\vee",
  "⋯": "\\cdots", "…": "\\ldots", "⋮": "\\vdots", "⋱": "\\ddots",
};

const REL_MAP: Record<string, string> = {
  "≠": "\\neq", "≈": "\\approx", "≡": "\\equiv", "∼": "\\sim",
  "≃": "\\simeq", "≅": "\\cong", "∝": "\\propto", "≤": "\\leq",
  "≥": "\\geq", "≪": "\\ll", "≫": "\\gg", "⊂": "\\subset",
  "⊆": "\\subseteq", "⊃": "\\supset", "∈": "\\in", "∉": "\\notin",
};

const ARROW_MAP: Record<string, string> = {
  "←": "\\leftarrow", "→": "\\rightarrow", "↔": "\\leftrightarrow",
  "⇐": "\\Leftarrow", "⇒": "\\Rightarrow", "⇔": "\\Leftrightarrow",
  "↦": "\\mapsto", "⟶": "\\longrightarrow", "↪": "\\hookrightarrow",
  "↠": "\\twoheadrightarrow", "↗": "\\nearrow", "↘": "\\searrow",
};

const BIGOP_MAP: Record<string, string> = {
  "∫": "\\int", "∬": "\\iint", "∭": "\\iiint", "∮": "\\oint",
  "∑": "\\sum", "∏": "\\prod", "∐": "\\coprod",
  "⋃": "\\bigcup", "⋂": "\\bigcap", "⋁": "\\bigvee",
  "⋀": "\\bigwedge", "⨁": "\\bigoplus",
};

/**
 * A slot's contents may be supplied either as ready-made tokens or, for
 * convenience in sample data / tests / the LaTeX parser, as a plain
 * string that is classified character-by-character into atom tokens.
 */
export type SlotInput = string | Token[];

/** Classify a single character into the closest atom token. */
function charToToken(ch: string): Token {
  if (/[0-9]/.test(ch)) return T.num(ch);
  if (/[a-z]/.test(ch)) return T.letter(ch);
  if (/[A-Z]/.test(ch)) return T.upletter(ch);
  if (GREEK_MAP[ch]) return T.greek(ch);
  if (ch === "+" || ch === "-" || ch === "−" || OP_MAP[ch]) return T.op(ch === "-" ? "−" : ch);
  if (ch === "=" || REL_MAP[ch]) return T.rel(ch);
  if (ARROW_MAP[ch]) return T.arrow(ch);
  if (BIGOP_MAP[ch]) return T.bigop(ch);
  return T.punct(ch);
}

/** Normalise a `SlotInput` into a `Token[]`. Arrays pass through. */
export function toSlot(input: SlotInput = ""): Token[] {
  if (Array.isArray(input)) return input;
  return [...input].map(charToToken);
}

export const T = {
  letter:   (g: string): Token => ({ kind: "letter",   g, italic: true }),
  upletter: (g: string): Token => ({ kind: "upletter", g, italic: false }),
  num:      (g: string): Token => ({ kind: "num",      g, italic: false }),
  punct:    (g: string): Token => ({ kind: "punct",    g, italic: false, mx: 0.18 }),
  op:       (g: string): Token => ({ kind: "op",       g, italic: false, mx: 0.28, cat: "ops" }),
  rel:      (g: string): Token => ({ kind: "rel",      g, italic: false, mx: 0.36, cat: "rel" }),
  arrow:    (g: string): Token => ({ kind: "arrow",    g, italic: false, mx: 0.45, cat: "arrows" }),
  greek:    (g: string): Token => ({ kind: "greek",    g, italic: true, cat: "greek" }),
  bigop:    (g: string): Token => ({ kind: "bigop",    g, italic: false, size: 1.4, mx: 0.2, cat: "bigops" }),
  fn:       (g: string): Token => ({ kind: "fn",       g, italic: false, mx: 0.18, cat: "fn" }),
  newline:  (): Token            => ({ kind: "newline" }),
  align:    (): Token            => ({ kind: "align" }),

  // Structures default to EMPTY slots so the user fills them in.
  // Slot inputs accept either a `Token[]` (the editor's path) or a
  // string (sample/test/parser convenience, classified per-char).
  frac:    (a: SlotInput = "", b: SlotInput = ""): StructToken => ({
    kind: "struct", structKind: "frac", name: "fraction",
    slots: { a: toSlot(a), b: toSlot(b) }, slotOrder: ["a", "b"],
  }),
  sqrt:    (a: SlotInput = ""): StructToken => ({
    kind: "struct", structKind: "sqrt", name: "sqrt",
    slots: { a: toSlot(a) }, slotOrder: ["a"],
  }),
  nthroot: (n: SlotInput = "", a: SlotInput = ""): StructToken => ({
    kind: "struct", structKind: "nthroot", name: "nth root",
    slots: { n: toSlot(n), a: toSlot(a) }, slotOrder: ["n", "a"],
  }),
  subsup:  (b: SlotInput = "", sub: SlotInput = "", sup: SlotInput = ""): StructToken => ({
    kind: "struct", structKind: "subsup", name: "sub/sup",
    slots: { b: toSlot(b), sub: toSlot(sub), sup: toSlot(sup) }, slotOrder: ["b", "sup", "sub"],
  }),
  integral: (lo: SlotInput = "", hi: SlotInput = "", body: SlotInput = ""): StructToken => ({
    kind: "struct", structKind: "integral", name: "integral",
    slots: { lo: toSlot(lo), hi: toSlot(hi), body: toSlot(body) }, slotOrder: ["lo", "hi", "body"],
  }),
  sumStruct: (lo: SlotInput = "", hi: SlotInput = "", body: SlotInput = ""): StructToken => ({
    kind: "struct", structKind: "sum", name: "sum",
    slots: { lo: toSlot(lo), hi: toSlot(hi), body: toSlot(body) }, slotOrder: ["lo", "hi", "body"],
  }),
  limStruct: (target: SlotInput = "", body: SlotInput = ""): StructToken => ({
    kind: "struct", structKind: "lim", name: "limit",
    slots: { target: toSlot(target), body: toSlot(body) }, slotOrder: ["target", "body"],
  }),
  matrix:    (r = 2, c = 2): StructToken => {
    // One slot per cell, indexed `r0c0`, `r0c1`, …, `r1c0`, … so the
    // Tab order reads left-to-right, top-to-bottom like a real
    // matrix. Cells start empty. Dimensions live on `rows`/`cols`.
    const slots: Record<string, Token[]> = {};
    const order: string[] = [];
    for (let i = 0; i < r; i++) for (let j = 0; j < c; j++) {
      const k = `r${i}c${j}`;
      slots[k] = [];
      order.push(k);
    }
    return {
      kind: "struct", structKind: "matrix", name: `${r}x${c} matrix`,
      slots, slotOrder: order, rows: r, cols: c,
    };
  },
  cases:     (): StructToken => ({
    kind: "struct", structKind: "cases", name: "cases",
    slots: { a: [], a_cond: [], b: [], b_cond: [] },
    slotOrder: ["a", "a_cond", "b", "b_cond"],
  }),
  aligned:   (): StructToken => ({
    kind: "struct", structKind: "aligned", name: "aligned",
    slots: { lhs1: [], rhs1: [], rhs2: [] },
    slotOrder: ["lhs1", "rhs1", "rhs2"],
  }),
  vector:    (): StructToken => ({
    kind: "struct", structKind: "vector", name: "vector",
    slots: { a: [], b: [], c: [] }, slotOrder: ["a", "b", "c"],
  }),
  // Single-slot delimited wrappers. Added 2026-05-28 per the
  // Common-panel research — both are top-of-list "frequently
  // authored, defaulted out" structures.
  abs:       (body: SlotInput = ""): StructToken => ({
    kind: "struct", structKind: "abs", name: "absolute value",
    slots: { body: toSlot(body) }, slotOrder: ["body"],
  }),
  parens:    (body: SlotInput = "", delim: StructToken["delim"] = "paren"): StructToken => ({
    kind: "struct", structKind: "parens", name: "parentheses",
    slots: { body: toSlot(body) }, slotOrder: ["body"], delim,
  }),
  // Accent mark over a single base slot. `cmd` is the LaTeX accent name
  // (hat/vec/bar/overline/tilde/dot/ddot/…) so the source round-trips.
  accent:    (cmd: string, base: SlotInput = ""): StructToken => ({
    kind: "struct", structKind: "accent", name: cmd,
    slots: { base: toSlot(base) }, slotOrder: ["base"], accent: cmd,
  }),
  // Binomial coefficient (n choose k) — a fraction without the rule,
  // wrapped in parentheses.
  binom:     (a: SlotInput = "", b: SlotInput = ""): StructToken => ({
    kind: "struct", structKind: "binom", name: "binomial",
    slots: { a: toSlot(a), b: toSlot(b) }, slotOrder: ["a", "b"],
  }),
};

export const SAMPLE_EQUATIONS: Record<string, Token[]> = {
  simple: [
    T.upletter("H"), T.punct(":"), T.upletter("X"), T.op("×"),
    T.upletter("["), T.num("0"), T.punct(","), T.num("1"), T.upletter("]"),
    T.arrow("⟶"), T.upletter("Y"),
  ],
  aligned: [
    T.letter("f"), T.upletter("("), T.letter("x"), T.upletter(")"),
    T.align(), T.rel("="), T.upletter("("), T.letter("x"), T.op("+"),
    T.num("1"), T.upletter(")"), T.op("·"), T.upletter("("), T.letter("x"),
    T.op("−"), T.num("1"), T.upletter(")"),
    T.newline(),
    T.align(), T.rel("="), T.letter("x"), T.subsup("", "", "2"), T.op("−"),
    T.num("1"), T.op("+"), T.letter("x"), T.op("−"), T.letter("x"),
    T.newline(),
    T.align(), T.rel("="), T.letter("x"), T.subsup("", "", "2"), T.op("−"),
    T.num("1"),
    T.newline(),
    T.align(), T.rel("≡"), T.upletter("P"), T.upletter("("), T.letter("x"),
    T.upletter(")"), T.punct(","),
  ],
  wide: [
    T.fn("det"), T.upletter("("), T.upletter("A"), T.op("−"), T.greek("λ"),
    T.upletter("I"), T.upletter(")"), T.rel("="), T.bigop("∑"),
    T.upletter("("), T.op("−"), T.num("1"), T.upletter(")"),
    T.subsup("", "", "σ"), T.bigop("∏"),
    T.subsup("a", "i,σ(i)", ""), T.rel("="), T.num("0"), T.punct(","),
    T.upletter("∀"), T.letter("λ"), T.op("∈"), T.upletter("ℂ"),
  ],
};

/** Emit the LaTeX for a single slot's token list. */
function slotLatex(slot: Token[] | undefined): string {
  return slot && slot.length ? equationToLatex(slot) : "";
}

/**
 * Build the LaTeX form of a structure from its slots. Empty slots
 * emit empty braces so the source compiles even on the first paint
 * — `\sqrt{}` is well-formed LaTeX, just renders as a blank
 * radicand. Users then fill the slot in.
 */
function structToLatex(t: StructToken): string {
  const s = t.slots;
  switch (t.structKind) {
    case "frac":     return `\\frac{${slotLatex(s.a)}}{${slotLatex(s.b)}}`;
    case "sqrt":     return `\\sqrt{${slotLatex(s.a)}}`;
    case "nthroot": {
      const n = slotLatex(s.n);
      return n ? `\\sqrt[${n}]{${slotLatex(s.a)}}` : `\\sqrt{${slotLatex(s.a)}}`;
    }
    case "subsup": {
      const base = slotLatex(s.b);
      const sub = slotLatex(s.sub);
      const sup = slotLatex(s.sup);
      if (!sub && !sup) return base;
      if (!sup) return `${base}_{${sub}}`;
      if (!sub) return `${base}^{${sup}}`;
      return `${base}_{${sub}}^{${sup}}`;
    }
    case "integral": return `\\int_{${slotLatex(s.lo)}}^{${slotLatex(s.hi)}} ${slotLatex(s.body)}`;
    case "sum":      return `\\sum_{${slotLatex(s.lo)}}^{${slotLatex(s.hi)}} ${slotLatex(s.body)}`;
    case "lim":      return `\\lim_{${slotLatex(s.target)}} ${slotLatex(s.body)}`;
    case "matrix": {
      const r = t.rows ?? 2;
      const c = t.cols ?? 2;
      const rows: string[] = [];
      for (let i = 0; i < r; i++) {
        const cells: string[] = [];
        for (let j = 0; j < c; j++) cells.push(slotLatex(s[`r${i}c${j}`]));
        rows.push(cells.join(" & "));
      }
      return `\\begin{pmatrix} ${rows.join(" \\\\ ")} \\end{pmatrix}`;
    }
    case "cases":
      return `\\begin{cases} ${slotLatex(s.a)} & ${slotLatex(s.a_cond)} \\\\ ${slotLatex(s.b)} & ${slotLatex(s.b_cond)} \\end{cases}`;
    case "aligned":
      return `\\begin{aligned} ${slotLatex(s.lhs1)} &= ${slotLatex(s.rhs1)} \\\\ &= ${slotLatex(s.rhs2)} \\end{aligned}`;
    case "vector":
      return `\\begin{pmatrix} ${slotLatex(s.a)} \\\\ ${slotLatex(s.b)} \\\\ ${slotLatex(s.c)} \\end{pmatrix}`;
    case "abs":
      return `\\left|${slotLatex(s.body)}\\right|`;
    case "parens": {
      const [lo, ro] = DELIMS[t.delim ?? "paren"] ?? DELIMS.paren;
      return `\\left${lo}${slotLatex(s.body)}\\right${ro}`;
    }
    case "accent":
      return `\\${t.accent ?? "hat"}{${slotLatex(s.base)}}`;
    case "binom":
      return `\\binom{${slotLatex(s.a)}}{${slotLatex(s.b)}}`;
    default:
      return "";
  }
}

function tokenToLatex(t: Token): string {
  if (t.kind === "newline") return " \\\\ ";
  if (t.kind === "align") return " & ";
  if (t.kind === "struct") return structToLatex(t as StructToken);
  if (t.kind === "greek") return GREEK_MAP[t.g ?? ""] ?? (t.g ?? "");
  if (t.kind === "op") return OP_MAP[t.g ?? ""] ?? (t.g ?? "");
  if (t.kind === "rel") return REL_MAP[t.g ?? ""] ?? (t.g ?? "");
  if (t.kind === "arrow") return ARROW_MAP[t.g ?? ""] ?? (t.g ?? "");
  if (t.kind === "bigop") return BIGOP_MAP[t.g ?? ""] ?? (t.g ?? "");
  if (t.kind === "fn") return "\\" + (t.g ?? "");
  // Uppercase Greek (Δ Γ Ψ …) and ℓ are inserted as `upletter` (upright),
  // so emit the LaTeX command for them rather than the raw Unicode glyph.
  if (GREEK_MAP[t.g ?? ""] !== undefined) return GREEK_MAP[t.g ?? ""];
  return t.g ?? "";
}

/** Emit a LaTeX source string for the current token list. */
export function equationToLatex(tokens: Token[]): string {
  let out = "";
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const piece = tokenToLatex(t);
    // Insert a space between two ident-runs so `\alpha b` doesn't
    // become `\alphab` when LaTeX glues them.
    if (i > 0 && /^[A-Za-z\\]/.test(piece) && /[A-Za-z]$/.test(out)) {
      out += " ";
    }
    out += piece;
  }
  return out;
}

/** Wrap into a labeled equation environment for the source tab / export. */
export function wrappedSource(tokens: Token[], label?: string): string {
  const body = equationToLatex(tokens);
  const lbl = label ? `\\label{${label}}` : "";
  return `\\begin{equation}${lbl}\n  ${body}\n\\end{equation}`;
}
